import React, { useContext } from "react";
import { IntlContext } from "context";
import { useParams } from "react-router";
import { AdFrequency } from "types/ad";
import { Loader } from "ui/Loader";
import { useOurAd } from "hooks/ad";
import { formatAdServiceLocation } from "helpers/formatAdServiceLocation";

import BackButton from "./BackButton";
import AdMainCard from "./AdMainCard";
import AdLocationCard from "./AdLocationCard";
import AdAdvertiserCard from "./AdAdvertiserCard";
import AdSidebar from "./AdSidebar";
import { buildRoute } from "routes";
import Seo from "components/Seo";

const AdsView: React.FC = () => {
    const intl = useContext(IntlContext);
    const { id } = useParams();

    const { data, isLoading, isError } = useOurAd(id);

    const backLabel = intl.formatMessage("ad.view.back_to_ads");

    if (isLoading) {
        return <Loader />;
    }

    if (isError || !data || !data.is_active) {
        return (
            <div className="min-h-screen bg-gray-50">
                <div className="max-w-6xl mx-auto px-4 py-6">
                    <BackButton label={backLabel} />
                    <div className="bg-white rounded-2xl shadow-sm p-6 sm:p-8 text-center">
                        <h1 className="text-2xl font-extrabold text-gray-900">
                            {intl.formatMessage("ad.view.unavailable")}
                        </h1>
                    </div>
                </div>
            </div>
        );
    }

    const adUrl = buildRoute.ad(data.id);
    const lat = Number(data.coordinates_lat);
    const lon = Number(data.coordinates_lon);
    const cityLabel = formatAdServiceLocation(data, false);
    const locationLabel = formatAdServiceLocation(data);
    const isRecurring = data.frequency === AdFrequency.RECURRING;

    return (
        <>
            <Seo
                title={intl.formatMessage("ad.seo.view.title", { title: data.title })}
                description={intl.formatMessage("ad.seo.view.description", {
                    category: intl.formatMessage(`category.${data.category.toString().toLocaleLowerCase()}`),
                    description: data.description?.slice(0, 160),
                })}
                canonical={adUrl}
                ogType="website"
            />
            <div className="min-h-screen bg-gray-50">
                <div className="max-w-6xl mx-auto px-4 py-6">
                    <BackButton label={backLabel} />

                    {/* two-column layout */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        <div className="lg:col-span-2 space-y-6">
                            <AdMainCard ad={data} isRecurring={isRecurring} cityLabel={cityLabel} />
                            <AdLocationCard lat={lat} lon={lon} locationLabel={locationLabel} />
                            <AdAdvertiserCard ad={data} />
                        </div>
                        <AdSidebar ad={data} />
                    </div>
                </div>
            </div>
        </>
    );
};

export default AdsView;
